import { useEffect } from 'react'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useProjectStore } from '../../../map-editor-shared/store/projectStore'
import { useCSVStore } from '../../../map-editor-features/csv/model/csvStore'
import type { ParseResult } from '../../../map-editor-features/csv/lib/csvParser'
import type { GroupedLayer } from '../../../map-editor-features/csv/lib/layerGrouper'

interface UseCSVAutoSaveParams {
  rawData: string | null
  parsedData: ParseResult | null
  groupedLayers: GroupedLayer[] | null
  selectedLayers: Set<string> | null
  loadedFileName: string | null
  currentFloor: string | null
  isRestoring: boolean
  updateFloorMapData: (floorId: string, data: any) => void
}

/**
 * Auto-saves CSV data to all floors in the current lot when CSV data changes.
 * Implements lot-scoped CSV sharing (all floors in same lot see the same CSV data).
 */
export const useCSVAutoSave = ({
  rawData,
  parsedData,
  groupedLayers,
  selectedLayers,
  loadedFileName,
  currentFloor,
  isRestoring,
  updateFloorMapData,
}: UseCSVAutoSaveParams) => {
  useEffect(() => {
    // Skip if no floor selected or during floor switching/restoration
    if (!currentFloor || isRestoring) {
      return
    }

    // Skip initial render
    if (!rawData && !parsedData) {
      return
    }

    // CRITICAL: Skip auto-save for restored data to prevent cross-floor contamination
    // Check if this is restored data (isRestored flag)
    const currentUploadState = useCSVStore.getState().uploadState
    if (currentUploadState.status === 'parsed' && (currentUploadState as any).isRestored) {
      console.log('⏭️ Skipping auto-save for restored data', { currentFloor })
      return
    }

    // Get all floors in the current lot using getState() to avoid infinite loop
    const allFloors = useFloorStore.getState().floors
    const lotId = useProjectStore.getState().currentLot
    const lotFloors = allFloors.filter(f => String(f.lotId) === String(lotId))


    // Save CSV data to ALL floors in current lot (not just current floor)
    // This ensures multi-floor CSV data is visible on all floors
    lotFloors.forEach(floor => {
      updateFloorMapData(floor.id, {
        csvRawData: rawData || undefined,
        csvParsedData: parsedData || undefined,
        csvGroupedLayers: groupedLayers || undefined,
        csvSelectedLayers: selectedLayers ? Array.from(selectedLayers) : [],
        csvFileName: loadedFileName || undefined,
      })
    })
  }, [rawData, parsedData, groupedLayers, selectedLayers, updateFloorMapData, loadedFileName, isRestoring, currentFloor])
}
